import { createStore } from "solid-js/store";
import type { MentionedVideoItem } from "../VideoMention";

/**
 * Page-scoped clipboard. State lives at module scope for the lifetime of the
 * page, so it is intentionally not preserved across navigations (each page load
 * starts with an empty clipboard). Because the store is a module singleton it is
 * shared across independent Solid render roots — notably the detached roots used
 * to hydrate mentioned-video lists inside rich-text answers.
 */

export type ClipboardVideoMention = {
	type: "video_mention";
	data: MentionedVideoItem;
};

/** Union of everything the clipboard can hold, grouped by `type`. */
export type ClipboardEntity = ClipboardVideoMention;

type ClipboardState = {
	/** Entities in the order they were added. */
	entities: ClipboardEntity[];
};

const [state, setState] = createStore<ClipboardState>({ entities: [] });

export const clipboardEntities = () => state.entities;

export const clipboardCount = () => state.entities.length;

export const clipboardIsEmpty = () => state.entities.length === 0;

export const clipboardVideoMentions = (): ClipboardVideoMention[] =>
	state.entities.filter(
		(entity): entity is ClipboardVideoMention =>
			entity.type === "video_mention",
	);

/**
 * Identity of a clip on the clipboard: source video plus time range, the same
 * rule as `questionBoxVideoMentionKey` and mentionCollector's `videoKey`. The
 * mention id will not do — two clips out of one video share a video id when
 * they come from evidence, and one clip cited twice gets a `vm_` id per event.
 */
function clipKey(mention: MentionedVideoItem): string {
	const video = mention.video_id ?? mention.id;
	return `${video}:${mention.start_time_sec ?? ""}:${mention.end_time_sec ?? ""}`;
}

export function isVideoMentionInClipboard(
	mention: MentionedVideoItem,
): boolean {
	const key = clipKey(mention);
	return state.entities.some(
		(entity) => entity.type === "video_mention" && clipKey(entity.data) === key,
	);
}

export function addVideoMentionToClipboard(mention: MentionedVideoItem): void {
	if (isVideoMentionInClipboard(mention)) {
		return;
	}
	setState("entities", (entities) => [
		...entities,
		{ type: "video_mention", data: mention },
	]);
}

export function removeVideoMentionFromClipboard(
	mention: MentionedVideoItem,
): void {
	const key = clipKey(mention);
	setState("entities", (entities) =>
		entities.filter(
			(entity) =>
				!(entity.type === "video_mention" && clipKey(entity.data) === key),
		),
	);
}

export function clearClipboard(): void {
	setState("entities", []);
}
